import { useInfiniteQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@mantine/hooks";
import { useMemo, useState } from "react";
import { fetchScopeOptions } from "@/api/access/userScope";
import type { ScopeOption, WritableBox } from "@/api/access/userScope";

export const ScopeOptionsQueryKey = "scopeOptions";

/**
 * The values a scope box offers in one entity, searched on the server and paged as the picker
 * is scrolled.
 *
 * `GET /access/scope/options` is the only source that is both entity-scoped and box-scoped. The
 * Add New User page's `/user-management/dropdown?type=exclusions` is not scoped to an entity and
 * uses a different vocabulary (it has `entity` and `dispatcher`, which the scope model has no
 * rule box for), so it cannot answer "what can this person be given here".
 */
export function useScopeOptions(
  tenantId: string | null,
  box: WritableBox | null,
  enabled: boolean,
) {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);

  const query = useInfiniteQuery({
    queryKey: [ScopeOptionsQueryKey, tenantId, box, debouncedSearch],
    enabled: enabled && tenantId !== null && box !== null,
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      fetchScopeOptions(
        { tenantId: tenantId!, box: box!, search: debouncedSearch || undefined, page: pageParam },
        signal,
      ),
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
  });

  const options = useMemo<ScopeOption[]>(() => {
    const seen = new Set<string>();
    const all: ScopeOption[] = [];
    for (const page of query.data?.pages ?? []) {
      for (const option of page.options) {
        if (seen.has(option.value)) continue;
        seen.add(option.value);
        all.push(option);
      }
    }
    return all;
  }, [query.data]);

  return {
    options,
    setSearch,
    isLoading: query.isPending || query.isFetchingNextPage,
    isError: query.isError,
    /** True only once a successful answer came back empty, so "none" is never a failed read. */
    isEmpty: query.isSuccess && options.length === 0 && debouncedSearch === "",
    isNoMatch: query.isSuccess && options.length === 0 && debouncedSearch !== "",
    hasMore: Boolean(query.hasNextPage),
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
}
